import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { Asset } from '../types'

/**
 * Global "currently selected images" state.
 * The website and the Miro panel mount the same provider, so selection
 * behaves identically in both.
 *
 * It holds the images themselves, not just their ids. Holding ids meant every
 * screen that wanted to show what was selected had to ask the database for
 * them again — and that request carries one id per selected image in its URL,
 * so selecting a few hundred built a request too long to send. The images are
 * already on screen when they are picked; keeping them costs nothing and
 * removes the round trip entirely.
 */
interface SelectionValue {
  selectedIds: string[]
  /** The selected images, in the order they were picked. */
  selectedAssets: Asset[]
  isSelected: (id: string) => boolean
  toggle: (asset: Asset) => void
  select: (asset: Asset) => void
  deselect: (id: string) => void
  /** Add every one of these, keeping anything already selected */
  selectMany: (assets: Asset[]) => void
  /** Remove every one of these, leaving the rest alone */
  deselectMany: (ids: string[]) => void
  clear: () => void
  count: number
}

const SelectionContext = createContext<SelectionValue | null>(null)

export function SelectionProvider({ children }: { children: ReactNode }) {
  // A Map rather than an array: insertion order is the order they were picked,
  // which is the order they are placed on the board.
  const [selected, setSelected] = useState<Map<string, Asset>>(new Map())

  const toggle = useCallback((asset: Asset) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(asset.id)) next.delete(asset.id)
      else next.set(asset.id, asset)
      return next
    })
  }, [])

  const select = useCallback((asset: Asset) => {
    setSelected((prev) => (prev.has(asset.id) ? prev : new Map(prev).set(asset.id, asset)))
  }, [])

  const deselect = useCallback((id: string) => {
    setSelected((prev) => {
      if (!prev.has(id)) return prev
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }, [])

  // Select all / clear all act on what is on screen, which is a filtered view,
  // so they add to and subtract from the selection rather than replacing it.
  const selectMany = useCallback((assets: Asset[]) => {
    setSelected((prev) => {
      const next = new Map(prev)
      for (const asset of assets) if (!next.has(asset.id)) next.set(asset.id, asset)
      return next
    })
  }, [])

  const deselectMany = useCallback((ids: string[]) => {
    setSelected((prev) => {
      const next = new Map(prev)
      for (const id of ids) next.delete(id)
      return next
    })
  }, [])

  const clear = useCallback(() => setSelected(new Map()), [])

  const value = useMemo<SelectionValue>(
    () => ({
      selectedIds: [...selected.keys()],
      selectedAssets: [...selected.values()],
      isSelected: (id: string) => selected.has(id),
      toggle,
      select,
      deselect,
      selectMany,
      deselectMany,
      clear,
      count: selected.size,
    }),
    [selected, toggle, select, deselect, selectMany, deselectMany, clear],
  )

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>
}

export function useSelection(): SelectionValue {
  const ctx = useContext(SelectionContext)
  if (!ctx) {
    throw new Error('useSelection must be used inside a <SelectionProvider>')
  }
  return ctx
}
